import React, { useCallback, useState } from 'react';
import { useCableState } from './hooks/useCableState';
import { useCableSelection } from './hooks/useCableSelection';
import { useAuth } from './context/AuthContext';
import { useUserSettings } from './context/UserSettingsContext';
import { Icons } from './components/icons/Icons';
import StandardCableForm from './components/forms/StandardCableForm';
import MultipolarCableForm from './components/forms/MultipolarCableForm';
import CablePreview from './components/visualization/CablePreview';
import { UnifiedCablesList } from './components/visualization/UnifiedCablesList';
// import { getCableDisplayName } from './utils/cableLogic';
import { validateCableFields, validateCable } from './utils/validation';
import { insertCableInOrder, compareSiglature, validateCableFields as validateCableFieldsUtil } from './utils/cableInsertion';
import GeneralSettingsSidebar from './components/ui/GeneralSettingsSidebar';
import QuantitiesForm from './components/forms/QuantitiesForm';
import SchematicCableView from './components/visualization/SchematicCableView';
import { Login } from './components/auth/Login';
import { Register } from './components/auth/Register';
import { LoginModal } from './components/auth/LoginModal';
import { RegisterModal } from './components/auth/RegisterModal';
import { UserSettingsPage } from './components/settings/UserSettingsPage';
import { LanguageToggle } from './components/ui/LanguageToggle';
import { useI18n } from './context/I18nContext';

function App() {
  const { t } = useI18n();
  // IMPORTANTE: Tutti gli hook devono essere chiamati PRIMA di qualsiasi return condizionale
  // per rispettare le regole degli hook di React
  const { isAuthenticated, isLoading: authLoading, logout, user } = useAuth();
  const { settings: userSettings } = useUserSettings(); // Impostazioni utente personalizzate
  const [showLogin, setShowLogin] = useState(true);
  
  // Chiamiamo useCableState sempre, anche se non lo useremo quando non autenticato
  // ma React richiede che gli hook siano chiamati nello stesso ordine sempre
  const { state, actions } = useCableState();
  const {
    isSelectionModeEnabled,
    setIsSelectionModeEnabled,
    selectedCables,
    toggleCableSelection,
    selectAllCables,
    deselectAllCables
  } = useCableSelection(state?.cables || []);

  const [warnings, setWarnings] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [keepAllSettings, setKeepAllSettings] = useState(true);
  const [showDeleteButtons, setShowDeleteButtons] = useState(new Set());
  const [scrollToCable, setScrollToCable] = useState({ siglatura: null, originalIndex: null });
  const [hoveredCable, setHoveredCable] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [listSortMode, setListSortMode] = useState('numerazione');
  
  // Stato per mostrare/nascondere il form di login modale
  const [showLoginModal, setShowLoginModal] = useState(false);
  // Stato per mostrare/nascondere pannello impostazioni utente
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);

  // Listener per il resize (opzionale, per gestire il responsive)
  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Chiudi modale quando l'utente si autentica
  React.useEffect(() => {
    if (isAuthenticated) {
      setShowLoginModal(false);
    }
  }, [isAuthenticated]);

  // Sincronizza terminali da userSettings quando disponibili
  React.useEffect(() => {
    if (userSettings?.terminal_data && Object.keys(userSettings.terminal_data).length > 0) {
      actions.setTerminali(userSettings.terminal_data);
    }
  }, [userSettings?.terminal_data]);

  const toggleDeleteButton = useCallback((originalIndex) => {
    setShowDeleteButtons(prev => {
      const newSet = new Set(prev);
      if (newSet.has(originalIndex)) {
        newSet.delete(originalIndex);
      } else {
        newSet.add(originalIndex);
      }
      return newSet;
    });
  }, []);

  const addMorsettoDirection = useCallback((field, direction, poloId) => {
    const arrow = direction === 'up' ? '▲' : '▼';

    if (typeof poloId === 'number') {
      actions.setCurrentMultipolarCable(prev => ({
        ...prev,
        poli: prev.poli.map(polo => {
          if (polo.id === poloId) {
            const currentText = polo[field] || '';
            const newText = `${currentText} ${arrow}`.trim();
            return { ...polo, [field]: newText };
          }
          return polo;
        })
      }));
    } else {
      actions.setCurrentCable(prev => ({
        ...prev,
        [field]: `${prev[field] || ''} ${arrow}`.trim()
      }));
    }
  }, [actions]);


  const handleAddMultipolarCable = useCallback(() => {
    const cable = state.currentMultipolarCable;

    const missingFields = validateCableFieldsUtil(cable, state.cables, state.settings.lingua);

    if (missingFields.length > 0) {
      const nice = missingFields.join(', ');
      setWarnings([t('validation.multipolarCableInsertedMissingFields', { fields: nice })]);
    }

    let finalCable = { ...cable };
    if (!finalCable.nomeCavo || finalCable.nomeCavo.trim() === '') {
      const poles = finalCable.poli.length;
      const section = finalCable.poli[0]?.sezione || (state.settings?.useAWG ? 20 : 0.5);
      finalCable.nomeCavo = `Cavo Multipolare ${poles}G${section}`;
    }

    if (state.editingMultipolarIndex >= 0) {
      // Salva modifiche su cavo esistente
      actions.setCables(prev => {
        const updated = [...prev];
        updated[state.editingMultipolarIndex] = { ...finalCable, originalIndex: state.editingMultipolarIndex };
        return updated;
      });
      actions.setEditingMultipolarIndex(-1);
      actions.resetCurrentMultipolarCable();
      setShowDeleteButtons(new Set());
    } else {
      // Aggiungi nuovo usando la funzione helper per inserire in ordine
      actions.addMultipolarCable(finalCable);
      const updatedCables = insertCableInOrder(finalCable, state.cables, state.settings);
      actions.setCables(updatedCables);
      actions.resetCurrentMultipolarCable();
    }
  }, [state.currentMultipolarCable, state.cables, state.settings, state.editingMultipolarIndex, actions, setWarnings, insertCableInOrder]);

  const handleCancelEdit = useCallback(() => {
    actions.resetCurrentMultipolarCable();
    actions.setEditingMultipolarIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleAddStandardCable = useCallback(() => {
    const missingFields = validateCableFields(state.currentCable, state.cables, state.settings.lingua);

    if (missingFields.length > 0) {
      const nice = missingFields.join(', ');
      setWarnings([t('validation.cableInsertedMissingFields', { fields: nice })]);
    }

    if (state.editingIndex >= 0) {
      const updatedCables = [...state.cables];
      updatedCables[state.editingIndex] = {
        ...state.currentCable,
        originalIndex: state.editingIndex
      };
      actions.setCables(updatedCables);
      actions.setEditingIndex(-1);
      setShowDeleteButtons(new Set());
    } else {
      // Usa la funzione helper per inserire in ordine
      const updatedCables = insertCableInOrder(state.currentCable, state.cables, state.settings);
      actions.setCables(updatedCables);
    }

    if (!keepAllSettings) {
      actions.resetCurrentCable();
    }
  }, [state.currentCable, state.cables, state.editingIndex, state.settings, actions, keepAllSettings, setWarnings, insertCableInOrder]);

  const swapAB = useCallback((poloId = null) => {
    if (poloId) {
      // Swap per polo multipolare
      actions.setCurrentMultipolarCable(prev => {
        const updatedPoli = prev.poli.map(polo => {
          if (polo.id === poloId) {
            return {
              ...polo,
              terminaleA: polo.terminaleB,
              terminaleB: polo.terminaleA,
              collegamentoA: polo.collegamentoB,
              collegamentoB: polo.collegamentoA
            };
          }
          return polo;
        });
        return { ...prev, poli: updatedPoli };
      });
    } else {
      // Swap per cavo standard
      actions.setCurrentCable(prev => ({
        ...prev,
        terminaleA: prev.terminaleB,
        terminaleB: prev.terminaleA,
        collegamentoA: prev.collegamentoB,
        collegamentoB: prev.collegamentoA
      }));
    }
  }, [actions]);

  const handleSaveAsNewStandardCable = useCallback(() => {
    const missingFields = validateCableFields(state.currentCable, state.cables, state.settings.lingua);

    if (missingFields.length > 0) {
      setWarnings(prev => [
        ...prev,
        `Attenzione: inserire ${missingFields.join(' e ')} per salvare come nuovo cavo.`
      ]);
    }
    
    // Usa la funzione helper per inserire in ordine
    const updatedCables = insertCableInOrder(state.currentCable, state.cables, state.settings);
    actions.setCables(updatedCables);
    actions.setEditingIndex(-1);
    setShowDeleteButtons(new Set());
    if (!keepAllSettings) {
      actions.resetCurrentCable();
    }
  }, [state.currentCable, state.cables, state.settings, actions, setWarnings, insertCableInOrder]);

  const handleEditStandardCable = useCallback((index) => {
    const cableToEdit = state.cables[index];
    if (!cableToEdit) return;
    actions.setCurrentCable({ ...cableToEdit });
    actions.setEditingIndex(index);
    actions.setEditingMultipolarIndex(-1);
    actions.setCableMode('standard');

    // Scroll al form con effetto visivo come nel file originale
    setTimeout(() => {
      const formElement = document.querySelector('#cable-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        formElement.style.transition = 'all 0.3s';
        formElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
        setTimeout(() => {
          formElement.style.boxShadow = '';
        }, 1500);
      }
    }, 100);
  }, [state.cables, actions]);

  const handleDeleteStandardCable = useCallback((index) => {
    actions.setCables(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((cable, newIndex) => ({
        ...cable,
        originalIndex: newIndex
      }));
    });
    actions.setEditingIndex(-1);
    actions.setEditingMultipolarIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleEditMultipolarCable = useCallback((index) => {
    const cableToEdit = state.cables[index];
    if (!cableToEdit) return;
    actions.setCurrentMultipolarCable(JSON.parse(JSON.stringify(cableToEdit)));
    actions.setEditingMultipolarIndex(index);
    actions.setEditingIndex(-1);
    actions.setCableMode('multipolar');

    // Scroll al form con effetto visivo come nel file originale
    setTimeout(() => {
      const formElement = document.querySelector('#cable-form');
      if (formElement) {
        formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        formElement.style.transition = 'all 0.3s';
        formElement.style.boxShadow = '0 0 20px rgba(59, 130, 246, 0.5)';
        setTimeout(() => {
          formElement.style.boxShadow = '';
        }, 1500);
      }
    }, 100);
  }, [state.cables, actions]);

  const handleDeleteMultipolarCable = useCallback((index) => {
    actions.setCables(prev => {
      const filtered = prev.filter((_, i) => i !== index);
      return filtered.map((cable, newIndex) => ({
        ...cable,
        originalIndex: newIndex
      }));
    });
    actions.setEditingMultipolarIndex(-1);
    actions.setEditingIndex(-1);
    setShowDeleteButtons(new Set());
  }, [actions]);

  const handleSaveAsNewMultipolarCable = useCallback(() => {
    const cable = state.currentMultipolarCable;
    let finalCable = { ...cable };
    if (!finalCable.nomeCavo || finalCable.nomeCavo.trim() === '') {
      const poles = finalCable.poli.length;
      const section = finalCable.poli[0]?.sezione || (state.settings?.useAWG ? 20 : 0.5);
      finalCable.nomeCavo = `Cavo Multipolare ${poles}G${section}`;
    }
    
    // Usa la funzione helper per inserire in ordine
    const updatedCables = insertCableInOrder(finalCable, state.cables, state.settings);
    actions.setCables(updatedCables);
    actions.setEditingMultipolarIndex(-1);
    actions.resetCurrentMultipolarCable();
    setShowDeleteButtons(new Set());
  }, [state.currentMultipolarCable, state.settings, actions, insertCableInOrder]);

  // Mostra loader solo mentre verifica autenticazione iniziale
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('cableList.loading')}</p>
        </div>
      </div>
    );
  }

  // Gestisce il click su Impostazioni: se non autenticato, mostra login
  const handleSettingsClick = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      setShowLogin(true);
    } else {
      setShowSettingsPanel(true);
    }
  };

  const handleLoginClick = () => {
    setShowLoginModal(true);
    setShowLogin(true);
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      <div className="w-full bg-black shadow-lg py-3 px-6 flex items-center justify-between">
        <div className="flex items-center">
          <Icons.CustomLogo className="h-12 w-auto" />
          <h1 className="ml-4 text-white text-2xl font-bold">WireCraft</h1>
        </div>
        <div className="flex items-center space-x-4">
          <LanguageToggle />
          {isAuthenticated ? (
            /* Menu dropdown per utente autenticato */
            <div className="relative group">
              <button className="flex items-center space-x-2 text-white hover:text-gray-300 transition-colors">
                <Icons.User className="w-6 h-6" />
                <span className="text-sm font-medium">{userSettings?.profile_data?.username || user?.email}</span>
                <Icons.ChevronDown className="w-4 h-4" />
              </button>
              
              {/* Dropdown menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="py-2">
                  <button
                    onClick={handleSettingsClick}
                    className="w-full px-4 py-2 text-left text-gray-700 hover:bg-gray-100 flex items-center space-x-2"
                  >
                    <Icons.Settings className="w-5 h-5" />
                    <span>{t('common.settings')}</span>
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={logout}
                    className="w-full px-4 py-2 text-left text-red-600 hover:bg-red-50 flex items-center space-x-2"
                  >
                    <Icons.X className="w-5 h-5" />
                    <span>{t('settings.logout')}</span>
                  </button>
                </div>
              </div>
            </div>
          ) : (
            /* Pulsanti per utente non autenticato */
            <div className="flex items-center space-x-2">
              <button
                onClick={handleSettingsClick}
                className="text-white hover:text-gray-300 transition-colors p-2 rounded-md hover:bg-gray-800"
                title={t('settings.settingsRequiresLogin')}
              >
                <Icons.Settings className="w-6 h-6" />
              </button>
              <button
                onClick={handleLoginClick}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
              >
                Accedi
              </button>
            </div>
          )}
        </div>
      </div>

      <main className="p-6 lg:p-8">
        {/* Impostazioni Generali - Spostate sopra le colonne principali */}
        <div className="max-w-7xl mx-auto mb-6 bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('settings.generalSettings')}</h2>
          <GeneralSettingsSidebar
            settings={state.settings}
            setSettings={actions.setSettings}
            currentCable={state.currentCable}
            setCurrentCable={actions.setCurrentCable}
            cables={state.cables}
            setCables={actions.setCables}
            setTerminali={actions.setTerminali}
            setFileName={actions.setFileName}
            setRevision={actions.setRevision}
            setSchemaElettrico={actions.setSchemaElettrico}
            setInstallazione={actions.setInstallazione}
          />
        </div>

        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-6"> {/* Modificato a xl:grid-cols-2 */}
          {/* Colonna di sinistra: Form Cavo Unificato */}
          <div className="lg:col-span-1"> {/* Modificato a lg:col-span-1 */}
            <div id="cable-form" className="bg-white p-6 rounded-lg shadow-md">
              {/* Header con selezione tipo cavo */}
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  {state.cableMode === 'standard' ? (
                    state.editingIndex >= 0 ? t('cableForm.editStandardCable') : t('cableForm.newCable')
                  ) : (
                    state.editingMultipolarIndex >= 0 ? t('cableForm.editMultipolarCable') : t('cableForm.newCable')
                  )}
                </h2>
                <select
                  value={state.cableMode}
                  onChange={(e) => actions.setCableMode(e.target.value)}
                  disabled={state.editingIndex >= 0 || state.editingMultipolarIndex >= 0}
                  className={`px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white text-gray-700 shadow-sm ${state.editingIndex >= 0 || state.editingMultipolarIndex >= 0 ? 'opacity-60 cursor-not-allowed' : ''
                    }`}
                >
                  <option value="standard">{t('cableForm.standardCable')}</option>
                  <option value="multipolar">{t('cableForm.multipolarCable')}</option>
                </select>
              </div>
              
              {(state.editingIndex >= 0 || state.editingMultipolarIndex >= 0) && (
                <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-sm text-blue-700">
                  {t('settings.modeLockedDuringEdit')}
                </div>
              )}
              {state.cableMode === 'standard' ? (
                <StandardCableForm
                  currentCable={state.currentCable}
                  handleInputChange={actions.handleInputChange}
                  settings={state.settings}
                  terminali={state.terminali}
                  addCable={handleAddStandardCable}
                  saveAsNewCable={handleSaveAsNewStandardCable}
                  editingIndex={state.editingIndex}
                  cancelEdit={() => actions.setEditingIndex(-1)}
                  keepAllSettings={keepAllSettings}
                  setKeepAllSettings={setKeepAllSettings}
                  setSuccessMessage={setSuccessMessage}
                  addMorsettoDirection={addMorsettoDirection}
                  swapAB={swapAB}
                  availableSheaths={state.availableSheaths}
                  onAddSheath={(sheath) => actions.setAvailableSheaths(prev => [...prev, sheath])}
                  onDeleteSheath={(sheathId) => {
                    // Rimuovi la guaina dalla lista
                    actions.setAvailableSheaths(prev => prev.filter(s => s.id !== sheathId));
                    // Rimuovi la guaina da tutti i cavi che la usano
                    actions.setCables(prev => prev.map(cable => 
                      cable.guaina === sheathId ? { ...cable, guaina: null } : cable
                    ));
                  }}
                />
              ) : (
                <MultipolarCableForm
                  currentMultipolarCable={state.currentMultipolarCable}
                  setCurrentMultipolarCable={actions.setCurrentMultipolarCable}
                  settings={state.settings}
                  onAddMultipolarCable={handleAddMultipolarCable}
                  onSaveAsNewMultipolarCable={handleSaveAsNewMultipolarCable}
                  editingMultipolarIndex={state.editingMultipolarIndex}
                  onCancelEdit={handleCancelEdit}
                  terminalData={state.terminali}
                  addPolo={actions.addPolo}
                  removePolo={actions.removePolo}
                  updatePolo={actions.updatePolo}
                  duplicatePolo={actions.duplicatePolo}
                  addMorsettoDirection={addMorsettoDirection}
                  swapAB={swapAB}
                  setSuccessMessage={setSuccessMessage}
                />
              )}
            </div>

            {warnings.length > 0 && (
              <div className="bg-yellow-100 border border-yellow-300 text-yellow-800 px-4 py-3 rounded relative flex items-start justify-between" role="alert">
                <div className="pr-4">
                  <strong className="font-bold">{t('messages.warning')}</strong>
                  <ul className="list-disc list-inside mt-2">
                    {warnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </div>
                <button
                  className="ml-4 text-yellow-800 hover:text-yellow-900"
                  onClick={() => setWarnings([])}
                  aria-label={t('messages.closeWarning')}
                >
                  ✕
                </button>
              </div>
            )}

            {successMessage && (
              <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">{t('messages.success')}</strong>
                <p className="text-sm">{successMessage}</p>
              </div>
            )}
          </div>

          {/* Colonna di destra: Elenco Cavi */}
          <div className="lg:col-span-1"> {/* Modificato a lg:col-span-1 */}
            <div className="bg-white p-6 rounded-lg shadow-md h-full flex flex-col">
              <UnifiedCablesList
                cables={state.cables}
                setCables={actions.setCables}
                setWarnings={setWarnings}
                editCable={handleEditStandardCable}
                editMultipolarCable={handleEditMultipolarCable}
                deleteCable={handleDeleteStandardCable}
                deleteMultipolarCable={handleDeleteMultipolarCable}
                settings={{ ...state.settings, setIsDragModeEnabled: (val) => actions.setSettings(prev => ({ ...prev, isDragModeEnabled: val })) }}
                setScrollToCable={setScrollToCable}
                setHoveredCable={setHoveredCable}
                isSelectionModeEnabled={isSelectionModeEnabled}
                setIsSelectionModeEnabled={setIsSelectionModeEnabled}
                selectedCables={selectedCables}
                toggleCableSelection={toggleCableSelection}
                selectAllCables={selectAllCables}
                deselectAllCables={deselectAllCables}
                editingIndex={state.editingIndex}
                editingMultipolarIndex={state.editingMultipolarIndex}

                // Pass new setters for file import
                setSettings={actions.setSettings}
                setRevision={actions.setRevision}
                setSchemaElettrico={actions.setSchemaElettrico}
                setInstallazione={actions.setInstallazione}
                setFileName={actions.setFileName}
                setGroupTitles={actions.setGroupTitles}
                setSuccessMessage={setSuccessMessage} // Pass setSuccessMessage
                setListSortMode={actions.setListSortMode} // Passa setListSortMode
                terminali={state.terminali} // Pass terminali for export
                availableSheaths={state.availableSheaths} // Pass availableSheaths
                setAvailableSheaths={actions.setAvailableSheaths} // Pass setter for import
              />
            </div>
          </div>
        </div>

        {/* Sezione form dedicata full-width per Quantità/Riepiloghi (come originale) */}
        {state.cables.length > 0 && (
          <div className="max-w-7xl mx-auto mt-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <QuantitiesForm 
                cables={state.cables} 
                settings={state.settings} 
                terminali={state.terminali}
                setWarnings={setWarnings}
                setSuccessMessage={setSuccessMessage}
              />
            </div>
          </div>
        )}

        {state.cables.length > 0 && (
          <div className="max-w-7xl mx-auto mt-6">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-semibold mb-4 text-gray-800">{t('schematic.schematicViewTitle')}</h2>
              <SchematicCableView
                cables={state.cables}
                settings={state.settings}
                setListSortMode={actions.setListSortMode}
                hoveredCable={hoveredCable}
                setHoveredCable={setHoveredCable}
                isMobile={isMobile}
                editCable={handleEditStandardCable}
                editMultipolarCable={handleEditMultipolarCable}
                deleteCable={handleDeleteStandardCable}
                deleteMultipolarCable={handleDeleteMultipolarCable}
                validateCable={(cable) => validateCable(cable, state.cables, state.settings.lingua)}
                editingIndex={state.editingIndex}
                editingMultipolarIndex={state.editingMultipolarIndex}
                showDeleteButtons={showDeleteButtons}
                toggleDeleteButton={toggleDeleteButton}
                isSelectionModeEnabled={isSelectionModeEnabled}
                selectedCables={selectedCables}
                availableSheaths={state.availableSheaths}
              />
            </div>
          </div>
        )}
      </main>

      {/* Modale Login/Register */}
      {showLoginModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setShowLoginModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={() => setShowLoginModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10"
            >
              <Icons.X className="w-6 h-6" />
            </button>
            <div className="p-6">
              {showLogin ? (
                <LoginModal onSwitchToRegister={() => setShowLogin(false)} />
              ) : (
                <RegisterModal onSwitchToLogin={() => setShowLogin(true)} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pagina Impostazioni Utente */}
      {showSettingsPanel && (
        <UserSettingsPage onClose={() => setShowSettingsPanel(false)} />
      )}

      <footer className="w-full bg-white shadow-inner py-4 px-6 mt-8 text-center text-gray-500 text-sm">
        <p>WireCraft © 2025, by BPA Electric</p>
        <div className="flex justify-center items-center gap-4 mt-2">
          <Icons.Wire className="w-6 h-6 text-gray-400" />
          <Icons.FileSpreadsheet className="w-6 h-6 text-gray-400" />
          <Icons.Download className="w-6 h-6 text-gray-400" />
        </div>
      </footer>
    </div>
  );
}

export default App;